import {useEffect, useState} from 'react';
import {Database, LogOut, RotateCcw, ShieldCheck, Store} from 'lucide-react';
import {ListingCard} from '../components/ListingCard';
import {date} from '../lib/format';
import {useAppLink, useIntent, type AccountIntent} from '../shell/AppLink';
import {useStore} from '../store/StoreContext';
import {INQUIRY_STATUS, ROLE, type Role} from '../store/types';

/** Mirrors the three accounts server/db/seed-data.ts installs on first boot. */
const DEMO_PASSWORD = '123456';
const DEMO_ACCOUNTS = [
  {email: 'an@moc.vn', name: 'Nguyễn Hoài An', role: 'nguoi-ban' as Role},
  {email: 'linh@moc.vn', name: 'Trần Mỹ Linh', role: 'nguoi-ban' as Role},
  {email: 'khach@moc.vn', name: 'Phạm Quốc Bảo', role: 'khach' as Role},
];
import {
  Button,
  Card,
  EmptyState,
  Field,
  FormGrid,
  Pill,
  Screen,
  Segmented,
  Select,
  TextInput,
} from '../ui/primitives';

type Tab = 'tai-khoan' | 'da-luu' | 'lien-he';

/**
 * Tài khoản — sign in, sign up, and the two lists that only exist per person:
 * what they saved and who they contacted.
 *
 * The sign-up form asks which side of the market they are on, because that
 * single answer decides whether the Quản lý app is theirs or not. Everything
 * else about the account can be changed later; nothing here blocks browsing.
 */
export function AccountApp() {
  const {
    inquiries,
    listings,
    profile,
    resetDemo,
    saved,
    signIn,
    signOut,
    signUp,
    updateProfile,
    user,
  } = useStore();
  const {open} = useAppLink();

  const [tab, setTab] = useState<Tab>('tai-khoan');
  const [form, setForm] = useState<'dang-nhap' | 'dang-ky'>('dang-nhap');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<Role>('khach');
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useIntent<AccountIntent>('account', (intent) => {
    if (intent.register) setForm('dang-ky');
    if (intent.tab) setTab(intent.tab);
  });

  useEffect(() => {
    if (user) setNotice(null);
  }, [user]);

  const submit = async () => {
    setBusy(true);
    const error =
      form === 'dang-nhap'
        ? await signIn(email, password)
        : await signUp({email, name, password, phone, role});
    setBusy(false);
    setNotice(error);
    if (!error) {
      setPassword('');
    }
  };

  /* ------------------------------------------------------------ signed out */

  if (!user) {
    return (
      <Screen>
        <div className="mx-auto max-w-lg space-y-4">
          <Card>
            <div className="mb-4 flex justify-center">
              <Segmented
                value={form}
                onChange={setForm}
                options={[
                  {label: 'Đăng nhập', value: 'dang-nhap'},
                  {label: 'Tạo tài khoản', value: 'dang-ky'},
                ]}
              />
            </div>

            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              {form === 'dang-ky' ? (
                <FormGrid>
                  <Field label="Họ và tên">
                    <TextInput value={name} onChange={setName} placeholder="Nguyễn Văn A" />
                  </Field>
                  <Field label="Số điện thoại">
                    <TextInput type="tel" value={phone} onChange={setPhone} placeholder="09xx xxx xxx" />
                  </Field>
                </FormGrid>
              ) : null}

              <Field label="Email">
                <TextInput value={email} onChange={setEmail} placeholder="ban@email.com" />
              </Field>
              <Field label="Mật khẩu" hint={form === 'dang-ky' ? 'Tối thiểu 6 ký tự' : undefined}>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="control min-h-11 w-full rounded-control px-3.5 text-[16px] @xl:min-h-9 @xl:text-[13px]"
                />
              </Field>

              {form === 'dang-ky' ? (
                <Field label="Bạn là" hint="Có thể đổi bất cứ lúc nào trong hồ sơ">
                  <Select
                    value={role}
                    onChange={(value) => setRole(value as Role)}
                    options={Object.entries(ROLE).map(([value, label]) => ({label, value}))}
                  />
                </Field>
              ) : null}

              {notice ? (
                <p className="sunken-sm rounded-control p-3 text-[12.5px] font-semibold text-[#a3262b]">
                  {notice}
                </p>
              ) : null}

              <Button tone="primary" type="submit" wide disabled={busy}>
                {form === 'dang-nhap' ? 'Đăng nhập' : 'Tạo tài khoản'}
              </Button>
            </form>
          </Card>

          <Card title="Tài khoản dùng thử">
            <p className="mb-3 text-[12.5px] text-ink-3">
              Ba tài khoản mẫu đã có sẵn trên máy chủ. Mật khẩu của cả ba là{' '}
              <strong className="font-semibold text-ink-2">{DEMO_PASSWORD}</strong>.
            </p>
            <ul className="space-y-2">
              {DEMO_ACCOUNTS.map((entry) => (
                <li
                  key={entry.email}
                  className="tile flex flex-wrap items-center gap-2 rounded-control px-3 py-2.5"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-ink">
                      {entry.email}
                    </span>
                    <span className="block text-[11.5px] text-ink-3">
                      {entry.name} · {ROLE[entry.role]}
                    </span>
                  </span>
                  <Button
                    onClick={() => {
                      setForm('dang-nhap');
                      setEmail(entry.email);
                      setPassword(DEMO_PASSWORD);
                    }}
                  >
                    Điền sẵn
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Screen>
    );
  }

  /* ------------------------------------------------------------- signed in */

  const savedListings = saved
    .map((id) => listings.find((listing) => listing.id === id))
    .filter((listing): listing is NonNullable<typeof listing> => Boolean(listing));
  const sent = inquiries.filter((inquiry) => inquiry.fromId === user.id);

  return (
    <Screen>
      <div className="mb-4 flex justify-center">
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            {label: 'Hồ sơ', value: 'tai-khoan'},
            {label: `Đã lưu (${savedListings.length})`, value: 'da-luu'},
            {label: `Đã liên hệ (${sent.length})`, value: 'lien-he'},
          ]}
        />
      </div>

      {tab === 'tai-khoan' ? (
        <div className="mx-auto max-w-2xl space-y-4">
          <Card>
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent text-[18px] font-bold text-accent-ink">
                {(profile?.name ?? user.email).slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold text-ink">{profile?.name ?? user.name}</p>
                <p className="truncate text-[12px] text-ink-3">{user.email}</p>
              </div>
              {profile?.verified ? (
                <Pill tone="violet">
                  <ShieldCheck size={12} className="mr-1" /> Đã xác minh
                </Pill>
              ) : null}
            </div>
          </Card>

          <Card title="Thông tin cá nhân">
            <FormGrid>
              <Field label="Họ và tên">
                <TextInput
                  value={profile?.name ?? ''}
                  onChange={(value) => void updateProfile({name: value})}
                />
              </Field>
              <Field label="Số điện thoại" hint="Dùng để điền sẵn khi bạn liên hệ hoặc đăng tin">
                <TextInput
                  type="tel"
                  value={profile?.phone ?? ''}
                  onChange={(value) => void updateProfile({phone: value})}
                />
              </Field>
              <Field label="Vai trò" wide>
                <Select
                  value={profile?.role ?? 'khach'}
                  onChange={(value) => void updateProfile({role: value as Role})}
                  options={Object.entries(ROLE).map(([value, label]) => ({label, value}))}
                />
              </Field>
            </FormGrid>
            <p className="mt-3 text-[12px] text-ink-3">
              Tham gia từ {profile ? date(profile.createdAt) : '—'}
            </p>
          </Card>

          <Card title="Dữ liệu & phiên đăng nhập">
            <p className="flex items-start gap-2 text-[12.5px] text-ink-2">
              <Database size={15} className="mt-0.5 shrink-0 text-ink-3" />
              Tin đăng, hồ sơ và liên hệ được lưu trên Postgres của máy chủ (đăng nhập bằng session
              cookie thật) — ảnh tải lên nằm trong trình duyệt của bạn.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button icon={<LogOut size={15} />} onClick={() => void signOut()}>
                Đăng xuất
              </Button>
              <Button icon={<RotateCcw size={15} />} onClick={() => void resetDemo()}>
                Khôi phục dữ liệu mẫu
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      {tab === 'da-luu' ? (
        savedListings.length === 0 ? (
          <>
            <EmptyState
              title="Chưa lưu tin nào"
              hint="Bấm hình trái tim trên một tin ở Thị trường để giữ lại đây."
            />
            <div className="mt-4 flex justify-center">
              <Button tone="primary" icon={<Store size={15} />} onClick={() => open('market')}>
                Mở Thị trường
              </Button>
            </div>
          </>
        ) : (
          <div className="grid gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
            {savedListings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                onOpen={() => open('market', {listingId: listing.id})}
              />
            ))}
          </div>
        )
      ) : null}

      {tab === 'lien-he' ? (
        sent.length === 0 ? (
          <EmptyState
            title="Bạn chưa gửi liên hệ nào"
            hint="Mở một tin đăng và bấm “Gửi liên hệ / đặt lịch xem nhà”."
          />
        ) : (
          <ul className="mx-auto max-w-2xl space-y-3">
            {sent.map((inquiry) => {
              const listing = listings.find((entry) => entry.id === inquiry.listingId);
              return (
                <li key={inquiry.id} className="surface-sm rounded-card p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill tone={INQUIRY_STATUS[inquiry.status].tone}>
                      {INQUIRY_STATUS[inquiry.status].label}
                    </Pill>
                    <span className="ml-auto text-[11.5px] text-ink-3">{date(inquiry.at)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => listing && open('market', {listingId: listing.id})}
                    className="mt-2 block w-full text-left"
                  >
                    <p className="truncate text-[13.5px] font-bold text-ink">
                      {listing?.title ?? 'Tin đã gỡ'}
                    </p>
                  </button>
                  <p className="mt-1 text-[13px] text-ink-2">{inquiry.message}</p>
                  {inquiry.visitAt ? (
                    <p className="mt-1 text-[11.5px] text-ink-3">
                      Hẹn xem nhà: {date(inquiry.visitAt)}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </Screen>
  );
}
